import {
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  output,
} from '@angular/core';

/** Size snapshot emitted by {@link ElementResizeDirective}. */
export type ElementResizeEvent = {
  readonly height: number;
  readonly width: number;
};

@Directive({
  selector: '[idtElementResize]',
})
/**
 * Observes the host element's box size via `ResizeObserver` and emits `resized`
 * with the rounded content-box size whenever it actually changes (layout
 * reflow, sidebar toggle, container resize) — finer grained than
 * {@link ResizeListenerDirective}, which only reacts to window resize.
 * Usable as `hostDirectives` to measure a component's own host element.
 *
 * Values are rounded to the nearest pixel and de-duplicated against the last
 * emission, so sub-pixel `ResizeObserver` noise and redundant callbacks with
 * an unchanged size don't trigger downstream signal updates.
 *
 * @example Template binding
 * ```html
 * <div idtElementResize (resized)="onResized($event)"></div>
 * ```
 * @example Host directive — inject and bridge the output
 * ```ts
 * // hostDirectives: [ElementResizeDirective]
 * private readonly elementResize = inject(ElementResizeDirective);
 * ```
 */
export class ElementResizeDirective {
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  /** Fired whenever the host element's rounded content box size changes. */
  readonly resized = output<ElementResizeEvent>();

  private lastEmitted: ElementResizeEvent | null = null;

  constructor() {
    const host = this.elementRef.nativeElement;
    const observer = new ResizeObserver((entries) => {
      const entry = entries.at(0);
      if (!entry) {
        return;
      }
      const height = Math.round(entry.contentRect.height);
      const width = Math.round(entry.contentRect.width);
      if (
        this.lastEmitted?.height === height &&
        this.lastEmitted.width === width
      ) {
        return;
      }
      this.lastEmitted = { height, width };
      this.resized.emit(this.lastEmitted);
    });
    observer.observe(host);
    this.destroyRef.onDestroy(() => observer.disconnect());
  }
}
